import { compact, difference, intersection, join } from '@quromedical/utils';
import type { FhirResource, Reference } from 'fhir/r4';

import type { BundleEntry, ResourceType } from '../types';
import type {
  CreateReference,
  VersionReference,
  VersionReferenceParts,
} from './reference';

/**
 * Get the split reference from a FHIR internal/relative into an object
 *
 * @param resourceReference
 * @returns `id` of the referred entity
 */
export const getReferenceData = (
  resourceReference?: Reference,
): Voidable<ReferenceData> => {
  const reference = resourceReference?.reference;
  if (!reference) {
    return undefined;
  }

  const [resource, id] = reference.split('/');
  const resourceType = resource as ResourceType;
  if (!resourceType || !id) {
    return undefined;
  }

  return {
    resourceType,
    id,
  };
};

const allDefined = (parts: Array<string | undefined>): parts is Array<string> =>
  compact(parts).length === parts.length;

/**
 * Check if a reference is a valid version reference
 *
 * This does not enforce the type of the expected resource as that should be handled externally
 * as the references can be of a single type or multiple types
 */
export const isVersionReference = <T extends FhirResource>(
  resourceReference?: Reference,
): resourceReference is VersionReference<T> => {
  const reference = resourceReference?.reference;

  if (!reference) {
    return false;
  }

  const parts = reference.split('/');

  const isRequiredLength = parts.length === 4;
  const isAllDefined = allDefined(parts);
  const hasHistory = parts[2] === '_history';

  return isRequiredLength && isAllDefined && hasHistory;
};

interface VersionReferenceData<T extends FhirResource> {
  resourceType: ResourceType<T>;
  id: string;
  version: string;
}

export const getVersionReferenceData = <T extends FhirResource>(
  resourceReference: VersionReference<T>,
): VersionReferenceData<T> => {
  const [resourceType, id, _history, version] =
    resourceReference.reference.split('/') as VersionReferenceParts<T>;

  return {
    resourceType,
    id,
    version,
  };
};

interface CreateVersionReferenceParams {
  id: string;
  version: string;
  display?: string;
}

export const createVersionReference = <T extends FhirResource>(
  resourceType: ResourceType<T>,
  params: CreateVersionReferenceParams,
): VersionReference<T> => ({
  reference: `${resourceType}/${params.id}/_history/${params.version}`,
  display: params.display,
});

/**
 * Get the split reference from a FHIR internal/relative reference `id`
 * @param reference
 * @returns `id` of the referred entity
 */
export const getReferenceId = (reference?: Reference): string | undefined => {
  const referenceData = getReferenceData(reference);

  return referenceData?.id;
};

export const getCreateReferenceId = (reference: CreateReference): string => {
  // the reference data for a CreateReference will always be defined
  const referenceData = getReferenceData(reference) as ReferenceData;

  return referenceData?.id;
};

export const isValidReference = (
  reference?: fhir4.Reference,
): reference is CreateReference => !!reference?.reference;

/**
 * Get the split reference from a FHIR internal/relative reference `id`
 * @param reference
 * @returns resourceType of the referred entity
 */
export const getReferenceType = (
  reference?: Reference,
): ResourceType | undefined => {
  const referenceData = getReferenceData(reference);

  return referenceData?.resourceType;
};

export const getResourceId = (resource?: {
  id?: string;
}): string | undefined => {
  const id = resource?.id;

  return id;
};

type NameDisplay =
  | string
  | {
      name: string;
      email: string;
    };

const getDisplay = (displayText?: NameDisplay): string | undefined => {
  if (!displayText) {
    return undefined;
  }

  if (typeof displayText === 'string') {
    return displayText;
  }

  const { name, email } = displayText;

  return `${name} <${email}>`;
};

/**
 * Create an internal/relative reference of the form 'Resource/id`. Specifying the reference's
 * display text means we don't need overhead for fetching this down the line
 *
 * @param resourceType
 * @param id
 * @param displayText the text displayed to refer to the referenced resource
 * @returns
 */
export const createReference = (
  resourceType: ResourceType,
  id: string,
  displayText?: string | NameDisplay,
): CreateReference => {
  const display = getDisplay(displayText);
  return {
    id,
    reference: join([resourceType, id], '/'),
    display,
  };
};

export const createReferenceFromResource = <
  TResource extends WithId<FhirResource>,
>(
  resource: TResource,
  displayText?: string | NameDisplay,
): CreateReference =>
  createReference(resource.resourceType, resource.id, displayText);

/**
 * Create a reference from an existing resource. Will return undefined if the resource does not
 * have a version ID
 */
export const createVersionReferenceFromResource = <
  TResource extends WithId<FhirResource>,
>(
  resource: TResource,
  display?: string,
): VersionReference<TResource> | undefined => {
  const version = resource.meta?.versionId;

  if (!version) {
    return undefined;
  }

  return createVersionReference<TResource>(resource.resourceType, {
    id: resource.id,
    version,
    display,
  });
};

export const createUrlReference = (url: string): Reference => ({
  reference: url,
});

/**
 * Create a Bundle reference ID the form of `urn:Resource:id`
 */
export const generateBundleReferenceId = (
  resourceType: ResourceType,
  key: string | number,
): string => join(['urn', resourceType, key], ':');

export interface BundleEntryWithUrl<T extends FhirResource>
  extends BundleEntry<T> {
  fullUrl: string;
}

/**
 * Create a Bundle reference ID the form of `urn:Resource:id`
 */
export const attachBundleFullUrl = <T extends FhirResource>(
  entry: BundleEntry<T>,
  key: string | number,
): BundleEntryWithUrl<T> => {
  const fullUrl = generateBundleReferenceId(entry.resource.resourceType, key);
  return {
    ...entry,
    fullUrl,
  };
};

export const createReferenceFromBundleEntry = <T extends FhirResource>(
  entry: BundleEntryWithUrl<T>,
): CreateReference => ({
  reference: entry.fullUrl,
});

/**
 * Create a Bundle reference in the form of `urn:Resource:id`
 * @param id any value that is unique for the given `resourceType` in a bundle
 */
export const createBundleReference = (
  resourceType: ResourceType,
  id: string | number,
): Reference => {
  const reference = generateBundleReferenceId(resourceType, id);
  return {
    reference,
  };
};

/**
 * Output a FHIR server compatible id query string for a multi-id search
 * @param ids
 */
export const getIdQueryString = (
  ids?: Array<string | undefined>,
): string | undefined => {
  const filtered = compact(ids);

  if (!filtered.length) {
    return undefined;
  }

  return join(filtered, ',');
};

/**
 * Check if the `id` param is truthy
 * @param resource
 * @returns
 */
export const hasId = (
  resource: FhirResource,
): resource is WithId<FhirResource> => !!resource.id;

export const isResourceType =
  <TResource extends FhirResource>(resourceType: ResourceType<TResource>) =>
  (resource: FhirResource): resource is TResource =>
    resource.resourceType === resourceType;

/**
 * retain values `ids` that are also in `comparer`
 * @returns values in `ids` which did not exist in `subtractIds`
 */
export const getCommon = (
  ids?: Array<string | undefined>,
  comparer?: Array<string | undefined>,
): string[] => {
  const compactIds = compact(ids);

  if (!ids) {
    return [];
  }

  const commonIds = intersection(compactIds, comparer);

  return compact(commonIds);
};

/**
 * remove `subtractIds` from `ids`
 * @returns values in `ids` which did not exist in `subtractIds`
 */
export const omitCommon = (
  ids?: Array<string | undefined>,
  subtractIds?: Array<string | undefined>,
): string[] => {
  const compactIds = compact(ids);

  if (!ids) {
    return [];
  }

  const differenceIds = difference(compactIds, subtractIds || []);

  return compact(differenceIds);
};

/**
 * Get the difference between the new and old state of an array of strings, insensitive to ordering
 * @returns ids that were added or removed, as well as if there have been any changes
 */
export const getDiff = (newIds: string[] = [], oldIds: string[] = []) => {
  const added = omitCommon(newIds, oldIds);
  const removed = omitCommon(oldIds, newIds);
  const hasChanged = !!(added.length || removed.length);

  return {
    added,
    removed,
    hasChanged,
  };
};

interface ReferenceData {
  id: string;
  resourceType: ResourceType;
}

export type Voidable<T> = T | undefined;

type WithId<T> = T & {
  id: string;
};

type Mapper<TIn, TOut> = (params: TIn) => TOut;

export const createMapWithId =
  <TIn, TOut>(mapper: Mapper<TIn, TOut>) =>
  (input: WithId<TIn>): WithId<TOut> => {
    const withoutId = mapper(input);

    return {
      ...withoutId,
      id: input.id,
    };
  };
const getReferenceIdFullUrl = (resourceReference?: Reference) => {
  const reference = resourceReference?.reference;
  if (!reference) {
    return undefined;
  }
  return reference.split(':').pop();
};

export const getResolvedReferenceId = (reference: Reference) => {
  return getReferenceId(reference)
    ? getReferenceId(reference)
    : getReferenceIdFullUrl(reference);
};
